/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { FlowConnection, Delete } from '@carbon/react/icons';
import { Button, Modal, TextInput, Tag } from 'carbon-components-react';
import _ from 'lodash';
import React, { Fragment, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';

import * as integrationsActions from '@ibm-watson/shared/modules/channels/store/channels/classic/actions';
import { ReadOnlyNotification } from '@ibm-watson/shared/modules/ia/components/access-control/ReadOnlyNotification';
import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { selectResourceGroupIdForPremiumPlan } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { getTenant } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppSelector } from '@ibm-watson/shared/store';
import { LoaderStates } from '../../../../../ts';
import * as integrationServerApi from '../../../api/integrations/integrationServer';
import {
  ServiceDeskLogo,
  getServiceDeskDescription
} from '../../../helpers/serviceDeskHelpers';
import { usePhoneFeatures } from '../../../hooks/useChannelFeatures';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { updateIntegration } from '../../../store/channels/actions';
import { selectDraftIntegration } from '../../../store/channels/selectors';
import { resetContactCenter } from '../../../store/phone/actions';
import { selectIntelepeerState } from '../../../store/phone/selectors';
import {
  getServiceDeskChannelName,
  AMAZON_CONNECT_BYOSD_TYPE,
  CONTACT_CENTER_BYOSD_TYPE,
  FREE_PHONE_BYOSD_TYPE,
  GENESYS_BYOSD_TYPE,
  INTELEPEER_BYOSD_TYPE,
  NICE_INCONTACT_BYOSD_TYPE,
  PHONE_CHANNEL_TYPE,
  TWILIO_BYOSD_TYPE,
  WEB_BYOSD_TYPE,
  GENESYS_AUDIO_CONNECTOR_TYPE
} from '../../../util/channelTypes';
import { ChannelContentProps } from '../../../util/types';
import { integrationBaseUrl } from '../../../util/urls';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { sharedMessages } from '../../messages';
import { messages as resetModalMessages } from '../../modals/messages';
import { ServiceDeskCard } from '../../service-desk/ServiceDeskCard';
import { AmazonConnectForm } from '../contact-center-tab/amazon-connect/AmazonConnectForm';
import { GenesysAudioConnectorForm } from '../contact-center-tab/genesys-audio-connector/GenesysAudioConnectorForm';
import { NiceCXoneForm } from '../contact-center-tab/nice-cxone/NiceCXoneForm';
import { links } from '../links';
import { removeAutoProvisionedNumber } from '../phone-helpers';
import { FIELDS } from '../schema';
import { FreePhoneNumber } from './FreePhoneNumber';
import { Connections as messages, ContactCenter } from './messages';
import { SipTrunk } from './SipTrunk';
import './Connections.scss';

// This is the list of Beta channels/connections used to show beta tag
const betaChannels = [];

export const ConnectionsTab = ({
  assistantId,
  environmentId,
  readOnly
}: ChannelContentProps) => {
  const dispatch = useDispatch();

  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration);
  const subscriptionId = useAppSelector(selectResourceGroupIdForPremiumPlan);
  const region = useAppSelector(getTenant)?.region;
  const intelepeerNumState = useAppSelector(selectIntelepeerState);

  const [showChangeServiceDesk, setShowChangeServiceDesk] =
    useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [deleteModalText, setDeleteModalText] = useState<string>('');

  const isNewIA = useIsNewIA();
  const contactCenterProvider = getFieldValue(FIELDS.CONTACT_CENTER.PROVIDER);
  const phoneNumbers = getFieldValue(FIELDS.PHONE_NUMBERS);
  const hasAutoProvisionedNum =
    !_.isNil(getFieldValue(FIELDS.FIRST_PHONE_NUMBER)) &&
    getFieldValue(FIELDS.FIRST_PHONE_NUMBER_AUTO_PROVISIONED);
  // Cannot delete connection / unsubscribe when an intelepeer number is provisioning
  const isProvisioningIntelepeerNum =
    contactCenterProvider === FREE_PHONE_BYOSD_TYPE &&
    intelepeerNumState === LoaderStates.ACTIVE &&
    !getFieldValue(FIELDS.FIRST_PHONE_NUMBER_PROVISION_SUCCESS);

  const USE_DELETE_MODAL_CONTACT_CENTER = [
    FREE_PHONE_BYOSD_TYPE,
    NICE_INCONTACT_BYOSD_TYPE,
    AMAZON_CONNECT_BYOSD_TYPE,
    GENESYS_AUDIO_CONNECTOR_TYPE
  ];

  const BYO_SIP_TRUNK_CONTACT_CENTER = [
    GENESYS_BYOSD_TYPE,
    INTELEPEER_BYOSD_TYPE,
    TWILIO_BYOSD_TYPE,
    WEB_BYOSD_TYPE
  ];

  /**
   * On mount, if there is no contact center configured, but there is
   * a provisioned number, we correct the contact center to be the
   * Free Phone contact center.
   */
  useMount(() => {
    if (contactCenterProvider === undefined && !_.isEmpty(phoneNumbers)) {
      const isIntelepeerNum = getFieldValue(
        FIELDS.FIRST_PHONE_NUMBER
      ).auto_provisioned;
      if (isIntelepeerNum) {
        updateFieldValue(FIELDS.CONTACT_CENTER.PROVIDER, FREE_PHONE_BYOSD_TYPE);
      }
    }
  });

  const openDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const resetServiceDesk = () => {
    if (USE_DELETE_MODAL_CONTACT_CENTER.includes(contactCenterProvider)) {
      openDeleteModal();
    } else if (BYO_SIP_TRUNK_CONTACT_CENTER.includes(contactCenterProvider)) {
      updateFieldValue(FIELDS.CONTACT_CENTER.PROVIDER, '');
    }
  };

  /**
   * Handle reset is called when Tanya has already set up a connection
   * with her phone integration and clicks 'delete integration' from a
   * free phone number, nice cxone, or amazon connect connection.
   */
  const handleReset = useCallback(async () => {
    // Reset and close the delete modal
    setDeleteModalText('');
    closeDeleteModal();
    const provider = getFieldValue(FIELDS.CONTACT_CENTER.PROVIDER);

    // If there exists an auto-provisioned number in the settings, we
    // must unsubscribe and then remove the number from the settings
    const updatedNumbers = _.cloneDeep(phoneNumbers);
    if (removeAutoProvisionedNumber(updatedNumbers)) {
      // unsubscribe the phone to de-provision the number
      if (isNewIA) {
        await integrationServerApi.unsubscribe(
          PHONE_CHANNEL_TYPE,
          draftIntegration.integration_id
        );
      } else {
        await integrationsActions.unsubscribeIntegration({
          integration: draftIntegration
        });
      }

      // post the phone number list update and contact center type
      // to the backend
      const updatedDraftIntegration = _.cloneDeep(draftIntegration);
      _.set(
        updatedDraftIntegration?.settings,
        FIELDS.PHONE_NUMBERS,
        updatedNumbers
      );
      _.set(
        updatedDraftIntegration?.settings,
        FIELDS.CONTACT_CENTER.PROVIDER,
        ''
      );
      dispatch(resetContactCenter());
      if (isNewIA) {
        await dispatch(
          updateIntegration({
            assistantId,
            environmentId,
            integration: updatedDraftIntegration
          })
        );
      } else {
        await integrationsActions.updateIntegration({
          assistantId,
          integrationId: draftIntegration.integration_id,
          integration: draftIntegration
        });
      }
      // update the draft integration phone numbers
      updateFieldValue(FIELDS.PHONE_NUMBERS, updatedNumbers);
    }

    // Reset these fields specific to the contact center
    switch (provider) {
      // Reset Nice CXOne contact ID invite header
      case NICE_INCONTACT_BYOSD_TYPE:
        updateFieldValue(FIELDS.CONTACT_CENTER.NICE_CXONE_CONTACT_ID, '');
        break;
      // Reset contact center ARN for Amazon Connect
      case AMAZON_CONNECT_BYOSD_TYPE:
        updateFieldValue(FIELDS.CONTACT_CENTER.ARN, '');
        break;
      case GENESYS_AUDIO_CONNECTOR_TYPE:
        updateFieldValue(FIELDS.GENESYS_AUDIO_CONNECTOR.API_KEY, undefined);
        updateFieldValue(
          FIELDS.GENESYS_AUDIO_CONNECTOR.CLIENT_SECRET,
          undefined
        );
        updateFieldValue(FIELDS.GENESYS_AUDIO_CONNECTOR.CONNECT_URL, undefined);
        break;
      default:
        break;
    }

    // Reset these fields for all contact centers
    updateFieldValue(FIELDS.SIP.CUSTOM_INVITE_HEADERS, []);
    updateFieldValue(FIELDS.CONTACT_CENTER.ACCESS_KEY_ID, '');
    updateFieldValue(FIELDS.CONTACT_CENTER.ACCESS_KEY_SECRET, '');
    updateFieldValue(FIELDS.CONTACT_CENTER.PROVIDER, '');
    // Reset all Redux states related to contact centers
    dispatch(resetContactCenter());
  }, [
    assistantId,
    closeDeleteModal,
    dispatch,
    draftIntegration,
    environmentId,
    getFieldValue,
    isNewIA,
    phoneNumbers,
    updateFieldValue
  ]);

  /**
   * Handles when the contact center service desk is changed.
   */
  const onTypeChanged = (serviceDesk: string) => {
    if (serviceDesk !== contactCenterProvider) {
      updateFieldValue(FIELDS.CONTACT_CENTER.PROVIDER, serviceDesk);
      ctaEvents.serviceDeskSelected({
        category: serviceDesk
      });
      setShowChangeServiceDesk(false);
    }
  };

  /**
   * Return the phone's SIP URI, built from the resource group ID
   * and region.
   *
   * @param {string} resourceGroupId
   * @param {string} region
   * @returns {string} the phone integrations' SIP URI
   */
  const getSipURI = (resourceGroupId: string, region: string): string =>
    integrationBaseUrl({
      channelType: PHONE_CHANNEL_TYPE,
      region,
      locale: resourceGroupId ?? 'public'
    });

  const learnMoreLink = isNewIA
    ? links.phoneLearnMoreNewIA
    : links.phoneLearnMoreClassicIA;

  const supportedChannels = usePhoneFeatures();

  return (
    supportedChannels.length > 0 && (
      <Fragment>
        <div className="Connections-ServiceDesk__choose-new">
          <TabContent
            ImageComponent={() => <FlowConnection size={32} />}
            title={t(messages.connections)}
            subtitle={t(messages.description)}
            learnMoreLink={learnMoreLink}>
            <div className="Connections Connections-ServiceDesk__config">
              {!contactCenterProvider ||
              contactCenterProvider === 'bring_your_own' ||
              showChangeServiceDesk ? (
                <div className="ServiceDesk__tiles">
                  <div className="ServiceDesk__tile-group">
                    {supportedChannels.map(serviceDesk => {
                      const isSupportedChannelBeta =
                        betaChannels.includes(serviceDesk);
                      // This below will render the Phone connection contact center clickable cards
                      return (
                        <ServiceDeskCard
                          readOnly={readOnly}
                          key={serviceDesk}
                          onClick={() => onTypeChanged(serviceDesk)}
                          parentChannelType={PHONE_CHANNEL_TYPE}
                          serviceDesk={serviceDesk}
                          hideLogo={false}
                          isBeta={isSupportedChannelBeta}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : (
                // This renders the contact center form with logo on top, description, instructions and the form
                <div>
                  <div className="Connections-ServiceDesk__title">
                    <ServiceDeskLogo
                      parentChannelType={PHONE_CHANNEL_TYPE}
                      serviceDeskType={contactCenterProvider}
                      showTitle={
                        contactCenterProvider === WEB_BYOSD_TYPE ||
                        contactCenterProvider === FREE_PHONE_BYOSD_TYPE
                      }
                      hideLogo={false}
                    />
                    {betaChannels.includes(contactCenterProvider) && (
                      <>
                        <Tag
                          type="cyan"
                          title={t(messages.beta)}
                          className="Connections-ServiceDesk__title-beta">
                          {t(messages.beta)}
                        </Tag>
                      </>
                    )}
                    <Button
                      className="Connections-ServiceDesk__change-other"
                      disabled={readOnly || isProvisioningIntelepeerNum}
                      hasIconOnly={false}
                      kind="tertiary"
                      size="sm"
                      aria-label={t(messages.deleteLabel)}
                      iconDescription={t(messages.deleteLabel)}
                      onClick={resetServiceDesk}>
                      <p>
                        <FormattedMessage {...messages.deleteLabel} />
                      </p>
                      <Delete
                        size={16}
                        aria-label={t(messages.deleteLabel)}
                        className="Connections-ServiceDesk__change-other-icon"
                      />
                    </Button>
                  </div>
                  {(contactCenterProvider === FREE_PHONE_BYOSD_TYPE ||
                    hasAutoProvisionedNum) && <FreePhoneNumber />}
                  {(contactCenterProvider === NICE_INCONTACT_BYOSD_TYPE ||
                    contactCenterProvider === GENESYS_AUDIO_CONNECTOR_TYPE ||
                    contactCenterProvider === AMAZON_CONNECT_BYOSD_TYPE ||
                    contactCenterProvider === CONTACT_CENTER_BYOSD_TYPE) && (
                    <>
                      <p className="TabContent__subtitle">
                        {getServiceDeskDescription(
                          'phone',
                          contactCenterProvider,
                          t
                        )}
                      </p>
                      <div className="ConnectToContactCenterStep__information">
                        <div className="ConnectToContactCenterStep__title TabContent__spacer16">
                          <FormattedMessage
                            {...ContactCenter.connectToContactCenterTitle}
                            values={{
                              contactCenter: getServiceDeskChannelName(
                                contactCenterProvider,
                                PHONE_CHANNEL_TYPE
                              )
                            }}
                          />
                        </div>
                        <div className="ConnectToContactCenterStep__instructions TabContent__spacer16">
                          <FormattedMessage
                            {...ContactCenter.connectToContactCenterInstruction}
                            values={{
                              contactCenter: getServiceDeskChannelName(
                                contactCenterProvider,
                                PHONE_CHANNEL_TYPE
                              )
                            }}
                          />
                        </div>
                        <div className="ConnectToContactCenterStep__note TabContent__spacer8">
                          <FormattedMessage
                            {...ContactCenter.connectToContactCenterNote}
                            values={{
                              contactCenter: getServiceDeskChannelName(
                                contactCenterProvider,
                                PHONE_CHANNEL_TYPE
                              )
                            }}
                          />
                        </div>
                        {contactCenterProvider ===
                          NICE_INCONTACT_BYOSD_TYPE && <NiceCXoneForm />}
                        {contactCenterProvider ===
                          AMAZON_CONNECT_BYOSD_TYPE && (
                          <>
                            <div className="ConnectToContactCenterStep__title">
                              <FormattedMessage
                                {...ContactCenter.connectToContactSetupInformation}
                              />
                            </div>
                            <AmazonConnectForm readOnly={readOnly} />
                          </>
                        )}
                        {contactCenterProvider ===
                          GENESYS_AUDIO_CONNECTOR_TYPE && (
                          <GenesysAudioConnectorForm readOnly={readOnly} />
                        )}
                      </div>
                    </>
                  )}
                  {BYO_SIP_TRUNK_CONTACT_CENTER.includes(
                    contactCenterProvider
                  ) && (
                    <SipTrunk
                      sipURI={getSipURI(subscriptionId, region)}
                      selectedServiceDesk={contactCenterProvider}
                    />
                  )}
                </div>
              )}
            </div>
          </TabContent>
        </div>
        {USE_DELETE_MODAL_CONTACT_CENTER.includes(contactCenterProvider) && (
          <Modal
            data-testid={'reset-service-desk-modal'}
            className="ServiceDeskResetModal"
            modalHeading={
              <FormattedMessage {...resetModalMessages.resetModalHeading} />
            }
            open={isDeleteModalOpen}
            primaryButtonText={<FormattedMessage {...sharedMessages.delete} />}
            secondaryButtonText={
              <FormattedMessage {...sharedMessages.cancel} />
            }
            onRequestSubmit={handleReset}
            onRequestClose={closeDeleteModal}
            primaryButtonDisabled={
              !(deleteModalText.toLocaleLowerCase() === 'delete')
            }
            danger>
            <div className="ServiceDeskResetModal__body">
              <ReadOnlyNotification readOnly={readOnly} />
              <FormattedMessage
                {...resetModalMessages.resetModalBody}
                values={{
                  serviceDeskName: getServiceDeskChannelName(
                    contactCenterProvider,
                    PHONE_CHANNEL_TYPE
                  )
                }}
              />
            </div>
            <div className="ServiceDeskResetModal__input">
              <TextInput
                readOnly={readOnly}
                id="ServiceDeskResetModal-input-text"
                value={deleteModalText}
                onChange={input => setDeleteModalText(input.target.value)}
                data-testid={'modal-confirmation-input'}
                labelText={
                  <FormattedMessage
                    {...resetModalMessages.deleteInputLabelText}
                  />
                }
                placeholder={t(resetModalMessages.deleteInputPlaceholderText)}
                disabled={readOnly}
              />
            </div>
          </Modal>
        )}
      </Fragment>
    )
  );
};
